// Screen: "Tournaments"
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTournaments, getTournamentDetail, registerForTournament } from '../apiClient';
import EloBadge from '../components/EloBadge';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const STATUS_COLORS = {
  registration: '#3fb950',
  active: '#f0883e',
  completed: '#8b949e'
};

const STATUS_LABELS = {
  registration: 'Open',
  active: 'In Progress',
  completed: 'Completed'
};

export default function TournamentsPage() {
  const { user, token } = useAuth();
  const isMobile = useIsMobile();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadTournaments = useCallback(async () => {
    const status = filter === 'all' ? null : filter;
    const data = await getTournaments(status);
    setTournaments(data.tournaments || []);
  }, [filter]);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const loadDetail = useCallback(async (id) => {
    const data = await getTournamentDetail(id);
    if (data) {
      setDetail(data);
      setSelectedTournament(id);
    }
  }, []);

  const handleRegister = async (tournamentId) => {
    setError('');
    setSuccess('');
    const result = await registerForTournament(token, tournamentId);
    if (result.success) {
      setSuccess('Registered successfully!');
      loadDetail(tournamentId);
      loadTournaments();
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  const isRegistered = detail?.players?.some(p => p.user_id === user?.userId);

  return (
    <>
    <SEO
      title="Buck Euchre Tournaments"
      description="Join Buck Euchre tournaments, compete against other players, and win Bucks. Daily, weekly, and special event tournaments."
      path="/tournaments"
    />
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Tournaments</h1>

      {error && (
        <div style={{ background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}
      {success && (
        <div style={{ background: '#1c3d2a', border: '1px solid #3fb950', color: '#3fb950', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{success}</div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'registration', 'active', 'completed'].map(f => (
          <button key={f} onClick={() => { setFilter(f); setSelectedTournament(null); setDetail(null); }} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: filter === f ? '#238636' : '#21262d',
            color: filter === f ? '#fff' : '#8b949e',
            border: filter === f ? 'none' : '1px solid #30363d'
          }}>
            {f === 'all' ? 'All' : STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Tournament Detail View */}
      {selectedTournament && detail ? (
        <div style={{ background: '#161b22', borderRadius: 12, padding: isMobile ? 20 : 28, border: '1px solid #30363d', marginBottom: 20 }}>
          <button onClick={() => { setSelectedTournament(null); setDetail(null); }} style={{
            background: 'transparent', border: 'none', color: '#58a6ff', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0
          }}>&larr; Back to list</button>

          <h2 style={{ fontSize: isMobile ? 18 : 24, marginBottom: 8 }}>{detail.tournament.name}</h2>
          {detail.tournament.description && (
            <p style={{ color: '#8b949e', fontSize: 13, marginBottom: 16 }}>{detail.tournament.description}</p>
          )}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <span style={{ fontSize: 12, color: STATUS_COLORS[detail.tournament.status], fontWeight: 600 }}>
              {STATUS_LABELS[detail.tournament.status]}
            </span>
            <span style={{ fontSize: 12, color: '#8b949e' }}>{detail.tournament.player_count}P {detail.tournament.format.replace('_', ' ')}</span>
            <span style={{ fontSize: 12, color: '#8b949e' }}>Max: {detail.tournament.max_players}</span>
            {detail.tournament.entry_fee > 0 && (
              <span style={{ fontSize: 12, color: '#fbbf24' }}>Entry: {detail.tournament.entry_fee} Bucks</span>
            )}
            {detail.tournament.prize_pool > 0 && (
              <span style={{ fontSize: 12, color: '#3fb950' }}>Prize: {detail.tournament.prize_pool} Bucks</span>
            )}
          </div>

          {/* Register button */}
          {detail.tournament.status === 'registration' && !isRegistered && (
            <button onClick={() => handleRegister(selectedTournament)} style={{
              background: '#238636', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', marginBottom: 20
            }}>
              {detail.tournament.entry_fee > 0 ? `Register (${detail.tournament.entry_fee} Bucks)` : 'Register'}
            </button>
          )}
          {isRegistered && detail.tournament.status === 'registration' && (
            <div style={{ background: '#1a3a1a', border: '1px solid #3fb950', color: '#3fb950', padding: '10px 16px', borderRadius: 8, marginBottom: 20, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
              You're registered! Waiting for tournament to start...
            </div>
          )}

          {/* Players */}
          <h3 style={{ fontSize: 15, marginBottom: 10, color: '#c9d1d9' }}>
            Players ({detail.players.length}/{detail.tournament.max_players})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            {detail.players.map((p, i) => (
              <div key={p.user_id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: '#0d1117', borderRadius: 6, border: '1px solid #21262d'
              }}>
                <span style={{ width: 20, textAlign: 'center', fontSize: 12, color: '#8b949e' }}>#{p.seed}</span>
                {p.profile_picture_url ? (
                  <img src={p.profile_picture_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#238636', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff', fontWeight: 700 }}>
                    {(p.nickname || '?')[0]}
                  </div>
                )}
                <span style={{ flex: 1, fontSize: 14, color: p.user_id === user?.userId ? '#58a6ff' : '#c9d1d9', fontWeight: p.user_id === user?.userId ? 600 : 400 }}>
                  {p.nickname || 'Player'}
                </span>
                <EloBadge points={p.rank_points ?? 0} preferredSuit={p.preferred_suit || '♠'} size="sm" />
                {p.status === 'eliminated' && (
                  <span style={{ fontSize: 10, color: '#f85149', fontWeight: 600 }}>OUT</span>
                )}
              </div>
            ))}
          </div>

          {/* Bracket */}
          {detail.matches.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, marginBottom: 10, color: '#c9d1d9' }}>Bracket</h3>
              {(() => {
                const rounds = {};
                detail.matches.forEach(m => {
                  if (!rounds[m.round_number]) rounds[m.round_number] = [];
                  rounds[m.round_number].push(m);
                });
                const playerMap = {};
                detail.players.forEach(p => { playerMap[p.user_id] = p.nickname || 'Player'; });

                return Object.entries(rounds).map(([round, matches]) => (
                  <div key={round} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 6, fontWeight: 600 }}>
                      {matches.length === 1 ? 'Final' : `Round ${round}`}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {matches.map(m => (
                        <div key={m.match_id} style={{
                          background: '#0d1117', border: '1px solid #21262d', borderRadius: 6, padding: '8px 12px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: 13 }}>
                              <span style={{ color: m.winner_id === m.player1_id ? '#3fb950' : '#c9d1d9', fontWeight: m.winner_id === m.player1_id ? 700 : 400 }}>
                                {playerMap[m.player1_id] || 'TBD'}
                              </span>
                              <span style={{ color: '#484f58', margin: '0 8px' }}>vs</span>
                              <span style={{ color: m.winner_id === m.player2_id ? '#3fb950' : '#c9d1d9', fontWeight: m.winner_id === m.player2_id ? 700 : 400 }}>
                                {playerMap[m.player2_id] || 'TBD'}
                              </span>
                            </div>
                            <span style={{ fontSize: 10, color: m.status === 'completed' ? '#3fb950' : '#8b949e' }}>
                              {m.status === 'completed' ? 'Done' : m.status === 'pending' && m.player1_id && m.player2_id ? 'Ready' : 'Waiting'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </>
          )}
        </div>
      ) : (
        /* Tournament List */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tournaments.length === 0 ? (
            <div style={{ background: '#161b22', borderRadius: 12, padding: 40, textAlign: 'center', border: '1px solid #30363d', color: '#484f58' }}>
              No tournaments {filter !== 'all' ? `with status "${STATUS_LABELS[filter]}"` : 'yet'}. Check back soon!
            </div>
          ) : tournaments.map(t => (
            <div key={t.tournament_id} onClick={() => loadDetail(t.tournament_id)} style={{
              background: '#161b22', borderRadius: 12, padding: isMobile ? 16 : 20,
              border: '1px solid #30363d', cursor: 'pointer',
              transition: 'border-color 0.2s'
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#58a6ff'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#30363d'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0 }}>{t.name}</h3>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: STATUS_COLORS[t.status] + '20', color: STATUS_COLORS[t.status]
                }}>
                  {STATUS_LABELS[t.status]}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#8b949e' }}>
                <span>{t.player_count}P</span>
                <span>{t.format.replace('_', ' ')}</span>
                <span>Max {t.max_players}</span>
                {t.entry_fee > 0 && <span style={{ color: '#fbbf24' }}>{t.entry_fee} Bucks entry</span>}
                {t.prize_pool > 0 && <span style={{ color: '#3fb950' }}>{t.prize_pool} Bucks prize</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
